import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { projectDataClient } from '../../../../lib/connect/clients';
import { useReextractContract } from './useReextractContract';
function isModificationFile(f) {
    const hay = [
        f.sourceKind,
        f.documentType,
        f.classificationLabel
    ].filter(Boolean).join(' ').toUpperCase();
    return hay.includes('MOD') || hay.includes('CONTRACT_MOD');
}
const uploadedMs = (f)=>f.uploadedAt ? Number(f.uploadedAt.seconds) * 1000 + f.uploadedAt.nanos / 1e6 : 0;
const isModificationType = (f)=>{
    const primary = f.classification?.primaryType?.toUpperCase() ?? '';
    return primary === 'MODIFICATION' || primary === 'AMENDMENT' || !primary && isModificationFile(f);
};
/**
 * What Re-extract reads: the latest document set only (SAD-962), never the
 * award by default. That is the most recently uploaded award or modification:
 * a modification is read again on its own change set, and the award only when
 * it is itself the latest upload. An attachment or addendum is read with the
 * award through its upload, never in its place. Only when nothing is
 * classified yet (an older upload) does the first non-modification document
 * stand in for the award, as before.
 */ export function latestSetTarget(files) {
    const withId = files.filter((f)=>f.baltigoDocId);
    if (!withId.some((f)=>f.classification?.primaryType)) {
        const award = withId.find((f)=>!isModificationFile(f)) ?? withId[0];
        return award ? {
            docId: award.baltigoDocId,
            kind: 'base'
        } : null;
    }
    const readable = withId.filter((f)=>f.classification?.primaryType?.toUpperCase() === 'BASE' || isModificationType(f));
    if (readable.length === 0) return null;
    const latest = readable.reduce((a, b)=>uploadedMs(b) > uploadedMs(a) ? b : a);
    return {
        docId: latest.baltigoDocId,
        kind: isModificationType(latest) ? 'modification' : 'base'
    };
}
/**
 * Re-runs contract extraction stages 4→7 against stored MinIO artifacts.
 * Visible to callers with program edit access (delivery_pm update / delivery_vp).
 */ export function ReextractPanel() {
    const { programId, program, canEdit } = useProgramContext();
    const { reextract, running } = useReextractContract();
    const zouProjectId = program?.zouProjectId ?? '';
    const { data: projectFiles } = useQuery({
        queryKey: [
            'projectFiles',
            zouProjectId
        ],
        queryFn: ()=>projectDataClient.listProjectFiles({
                projectId: zouProjectId
            }),
        enabled: !!zouProjectId && canEdit,
        staleTime: 30000
    });
    const target = useMemo(()=>latestSetTarget(projectFiles?.files ?? []), [
        projectFiles?.files
    ]);
    if (!canEdit || !zouProjectId) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm font-medium text-foreground",
                        children: "Re-extract contract data"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: "Re-reads the latest upload against its stored artifacts (skips PDF conversion). Anything it reads differently from what is on file comes back as review items."
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraButton, {
                type: "button",
                variant: "secondary",
                size: "sm",
                disabled: running || !target,
                onClick: ()=>target && void reextract({
                        zouProjectId,
                        programId,
                        reextractDocId: target.docId,
                        documentKind: target.kind,
                        label: target.kind === 'modification' ? 'Re-extracting the latest modification' : 'Re-extracting contract',
                        link: target.kind === 'modification' ? `/delivery/programs/${programId}/workroom?tab=modifications` : `/delivery/programs/${programId}/docs`
                    }),
                "data-testid": "delivery-operational-reextract-button",
                children: [
                    /*#__PURE__*/ _jsx(RefreshCw, {
                        className: `mr-2 h-4 w-4 ${running ? 'animate-spin' : ''}`
                    }),
                    running ? 'Starting…' : 'Re-extract'
                ]
            })
        ]
    });
}
